import React from "react";
import { FiChevronRight, FiFolder, FiGrid } from "react-icons/fi";
import type { StorageFolder } from "../../API";
import { ROOT_PATHS, ROOT_PLACEHOLDER } from "./types";
import { sortFolders } from "./utils";

export interface FolderTreeProps {
	/** Dossier courant (ROOT_PLACEHOLDER à la racine). */
	currentPath: string;
	/** Fil d'ariane du dossier courant (browse) : dessine la descente sous la racine active. */
	breadcrumbs: StorageFolder[];
	/** Sous-dossiers du dossier courant (browse). */
	childFolders: StorageFolder[];
	onNavigate: (path: string) => void;
}

const itemClass = (active: boolean) =>
	`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-semibold transition ${
		active ? "bg-[#005CA9]/10 text-[#005CA9]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
	}`;

/**
 * Rail secondaire de navigation par dossiers : « Tous les médias », les trois racines du bucket,
 * puis : sous la racine active : la descente (fil d'ariane) et les sous-dossiers du dossier
 * courant. L'arborescence complète n'existe pas côté API : le rail montre le chemin réellement
 * parcouru plus les enfants directs, ce qui suffit pour naviguer sans quitter la vue.
 */
export const FolderTree: React.FC<FolderTreeProps> = ({ currentPath, breadcrumbs, childFolders, onNavigate }) => {
	const isRoot = currentPath === ROOT_PLACEHOLDER;
	const activeRoot = isRoot ? null : currentPath.split("/")[0] ?? null;
	// Descente au-delà de la racine (la racine elle-même est déjà un item du rail).
	const descent = breadcrumbs.filter((crumb) => (crumb.path ?? "").includes("/"));
	const children = sortFolders(childFolders.filter((folder) => Boolean(folder?.path)));

	return (
		<nav aria-label="Dossiers" className="w-56 shrink-0 space-y-0.5 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
			<button type="button" onClick={() => onNavigate(ROOT_PLACEHOLDER)} aria-current={isRoot ? "true" : undefined} className={itemClass(isRoot)}>
				<FiGrid className="h-3.5 w-3.5 shrink-0" aria-hidden /> Tous les médias
			</button>
			{ROOT_PATHS.map((root) => {
				const isActiveRoot = activeRoot === root.path;
				const isCurrent = currentPath === root.path;
				return (
					<React.Fragment key={root.path}>
						<button
							type="button"
							onClick={() => onNavigate(root.path)}
							aria-current={isCurrent ? "true" : undefined}
							className={itemClass(isCurrent)}
						>
							<FiFolder className="h-3.5 w-3.5 shrink-0" aria-hidden /> {root.label}
						</button>
						{isActiveRoot && (
							<div className="space-y-0.5">
								{descent.map((crumb, index) => {
									const crumbPath = crumb.path ?? "";
									const isLast = crumbPath === currentPath;
									return (
										<button
											key={crumb.id ?? crumbPath}
											type="button"
											onClick={() => crumbPath && onNavigate(crumbPath)}
											aria-current={isLast ? "true" : undefined}
											style={{ paddingLeft: `${1.25 + index * 0.75}rem` }}
											className={itemClass(isLast)}
										>
											<FiChevronRight className="h-3 w-3 shrink-0 text-slate-300" aria-hidden />
											<span className="truncate">{crumb.name ?? crumbPath.split("/").pop()}</span>
										</button>
									);
								})}
								{children.map((folder) => {
									const folderPath = folder.path ?? "";
									return (
										<button
											key={folder.id ?? folderPath}
											type="button"
											onClick={() => folderPath && onNavigate(folderPath)}
											style={{ paddingLeft: `${1.25 + (descent.length + 1) * 0.75}rem` }}
											className={itemClass(false)}
										>
											<FiFolder className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
											<span className="truncate">{folder.name ?? folderPath.split("/").pop()}</span>
										</button>
									);
								})}
							</div>
						)}
					</React.Fragment>
				);
			})}
		</nav>
	);
};
